'use client';

import { useState } from 'react';

interface NewsletterFormProps {
  /** Identifies where the subscription came from (shown in the Telegram alert). */
  source?: string;
  /**
   * 'footer' — compact inline style used in the site footer.
   * 'card'   — larger centered style used in marketing/blog CTA cards.
   */
  variant?: 'footer' | 'card';
}

/**
 * Reusable newsletter subscribe form. Posts the email to
 * /api/newsletter-subscribe, which notifies us on Telegram.
 */
export default function NewsletterForm({ source = 'website', variant = 'footer' }: NewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    setMessage('');
    try {
      const res = await fetch('/api/newsletter-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Something went wrong.');
      setStatus('success');
      setMessage("You're subscribed! Thanks for joining.");
      setEmail('');
    } catch (err: any) {
      setStatus('error');
      setMessage(err?.message || 'Failed to subscribe. Please try again.');
    }
  };

  const disabled = status === 'loading' || status === 'success';

  if (variant === 'card') {
    return (
      <div className="max-w-md mx-auto">
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={disabled}
            placeholder="Enter your email"
            className="flex-1 px-5 py-3.5 rounded-xl bg-white/[0.03] border border-white/[0.12] text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={disabled}
            className="px-8 py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold transition-all duration-300 shadow-lg shadow-purple-500/30 hover:-translate-y-0.5 whitespace-nowrap disabled:opacity-60 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
          >
            {status === 'loading' ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Subscribing…
              </>
            ) : status === 'success' ? (
              'Subscribed!'
            ) : (
              'Subscribe'
            )}
          </button>
        </form>
        {message && (
          <p className={`text-sm mt-3 ${status === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{message}</p>
        )}
      </div>
    );
  }

  // footer variant
  return (
    <div className="w-full md:w-auto">
      <form onSubmit={handleSubmit} className="flex gap-2 w-full md:w-auto">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={disabled}
          placeholder="Enter your email"
          className="px-4 py-2 bg-[#1A1F35] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-600 flex-1 md:w-64 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={disabled}
          className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'loading' ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : status === 'success' ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          )}
        </button>
      </form>
      {message && (
        <p className={`text-xs mt-2 ${status === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{message}</p>
      )}
    </div>
  );
}
